import type { ToggleButtonGroupProps } from 'src/UIKit/components/ToggleButtonGroup';
import { SxProps, Theme } from "@mui/material";
import React, { ReactElement, ReactNode } from "react";
import { Control, Controller, FieldValues, Path, PathValue, RegisterOptions, UseFormReturn } from "react-hook-form";
import ToggleButtonGroup from 'src/UIKit/components/ToggleButtonGroup';
import { SelectOption } from "src/UIKit/components/types";

interface FormToggleButtonGroupProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  fieldName: Path<T>;

  helperText?: ReactNode;
  validation?: RegisterOptions;
  options: SelectOption<PathValue<T, Path<T>>, ReactNode>[];

  disabled?: boolean;
  disableUnselect?: boolean;
  size?: ToggleButtonGroupProps<string, unknown>['size'];
  color?: "standard" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  margin?: "dense" | "normal" | "none";
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

export const FormToggleButtonGroup = <T extends FieldValues>(
  props: FormToggleButtonGroupProps<T>
): ReactElement | null => {
  const {
    form,
    fieldName,
    helperText,
    validation,
    options,
    disabled,
    disableUnselect,
    size,
    color,
    margin,
    fullWidth = true,
    sx,
  } = props;

  return (
    <Controller
      name={fieldName}
      control={form.control as Control<FieldValues>}
      rules={validation}
      render={({ field, fieldState }) => {
        const { value, onChange, ...rest } = field;
        const { error } = fieldState;

        return (
          <ToggleButtonGroup
            {...rest}
            value={value}
            options={options}
            onChange={(_, value) => onChange(value)}
            error={!!error}
            helperText={error?.message || helperText}
            disabled={disabled}
            disableUnselect={disableUnselect}
            size={size}
            color={color}
            margin={margin}
            fullWidth={fullWidth}
            sx={sx}
          />
        );
      }}
    />
  );
};
